/**
 * FormSchema Override Diff Panel
 *
 * Visual diff inspector for FormSchema base vs tenant override.
 * Shows field-level changes, raw JSON comparison, and safety analysis.
 *
 * Integrated as "Override Inspector" tab in FormSchemaAdminPage.
 */

import { useState } from 'react';
import {
  Alert,
  Badge,
  Code,
  Group,
  Paper,
  SimpleGrid,
  Stack,
  Switch,
  Tabs,
  Text,
  ThemeIcon,
} from '@mantine/core';
import {
  IconAlertTriangle,
  IconBraces,
  IconEye,
  IconShieldCheck,
} from '@tabler/icons-react';
import { DiffTreeViewer } from '../components/DiffTreeViewer';
import { useFormSchemaComposition } from '../hooks/useFormSchemaComposition';
import type { AffectedField, SafetyWarning } from '../hooks/useFormSchemaComposition';
import { syntaxHighlight } from '../utils/syntax-highlight';

// =============================================================================
// TYPES
// =============================================================================

/** Field shape — accepts both generated enums and plain strings for fieldType */
interface DiffPanelField {
  name: string;
  fieldType: string | { toString(): string };
  label?: string | null;
  required?: boolean | null;
  piiClassification?: string | null;
  auditRequired?: boolean | null;
  minimumTier?: string | null;
}

export interface FormSchemaOverrideDiffPanelProps {
  /** The merged FormSchema (as returned by formSchema query). Accepts generated FormSchema type. */
  schema: {
    entityType: string;
    subgraph: string;
    version: string;
    displayName: string;
    safetyLevel: string | { toString(): string };
    fields: DiffPanelField[];
    [key: string]: unknown;
  };
  /** The raw tenant override (null if none). Accepts generated FormSchemaOverride type. */
  override: {
    hiddenFields?: string[] | null;
    readonlyFields?: string[] | null;
    labelOverrides?: string | null;
    customFields?: DiffPanelField[] | null;
    customValidations?: unknown[] | null;
    [key: string]: unknown;
  } | null;
}

// =============================================================================
// COMPONENT
// =============================================================================

export function FormSchemaOverrideDiffPanel({ schema, override }: FormSchemaOverrideDiffPanelProps) {
  const composition = useFormSchemaComposition(schema, override);
  const [expandedPaths, setExpandedPaths] = useState<Record<string, boolean>>({});
  const [showOnlyChanges, setShowOnlyChanges] = useState(true);

  const handleTogglePath = (path: string) => {
    setExpandedPaths(prev => ({ ...prev, [path]: !(prev[path] ?? true) }));
  };

  if (!composition.hasOverride) {
    return (
      <Paper p="xl" withBorder>
        <Stack align="center" gap="md">
          <ThemeIcon size="xl" variant="light" color="gray">
            <IconEye size={24} />
          </ThemeIcon>
          <Text c="dimmed" ta="center">
            No tenant overrides applied — base schema is active.
          </Text>
          <Text size="xs" c="dimmed" ta="center">
            Use the "Manual Override" tab to customize this form for your tenant.
          </Text>
        </Stack>
      </Paper>
    );
  }

  return (
    <Stack gap="md">
      {/* Summary Badges */}
      <Paper p="sm" withBorder>
        <Group gap="xs">
          <Text size="sm" fw={500}>Override Summary:</Text>
          {composition.hiddenCount > 0 && (
            <Badge size="sm" color="red" variant="light">
              {composition.hiddenCount} hidden
            </Badge>
          )}
          {composition.readonlyCount > 0 && (
            <Badge size="sm" color="orange" variant="light">
              {composition.readonlyCount} readonly
            </Badge>
          )}
          {composition.labelOverrideCount > 0 && (
            <Badge size="sm" color="blue" variant="light">
              {composition.labelOverrideCount} label {composition.labelOverrideCount === 1 ? 'override' : 'overrides'}
            </Badge>
          )}
          {composition.customFieldCount > 0 && (
            <Badge size="sm" color="green" variant="light">
              {composition.customFieldCount} custom {composition.customFieldCount === 1 ? 'field' : 'fields'}
            </Badge>
          )}
        </Group>
      </Paper>

      {/* Safety Warnings */}
      {composition.safetyWarnings.length > 0 && (
        <Alert color="red" icon={<IconAlertTriangle size={16} />}>
          <Text size="sm" fw={500}>Safety Warnings</Text>
          {composition.safetyWarnings.map((w: SafetyWarning) => (
            <Text key={`${w.fieldName}-${w.changeType}`} size="xs" mt={4}>
              {w.message}
            </Text>
          ))}
        </Alert>
      )}

      {/* Sub-tabs */}
      <Tabs defaultValue="visual">
        <Tabs.List>
          <Tabs.Tab value="visual" leftSection={<IconEye size={14} />}>
            Visual Diff
          </Tabs.Tab>
          <Tabs.Tab value="json" leftSection={<IconBraces size={14} />}>
            Raw JSON
          </Tabs.Tab>
          <Tabs.Tab value="safety" leftSection={<IconShieldCheck size={14} />}>
            Safety Analysis
          </Tabs.Tab>
        </Tabs.List>

        {/* Visual Diff */}
        <Tabs.Panel value="visual" pt="md">
          <Stack gap="sm">
            <Group justify="space-between">
              <Text size="sm" fw={500}>Field Changes</Text>
              <Switch
                size="xs"
                label="Show only changes"
                checked={showOnlyChanges}
                onChange={(e) => setShowOnlyChanges(e.currentTarget.checked)}
              />
            </Group>
            {composition.fieldDiff && (
              <Paper p="sm" withBorder style={{ fontFamily: 'monospace', fontSize: 12 }}>
                <DiffTreeViewer
                  node={composition.fieldDiff}
                  showOnlyChanges={showOnlyChanges}
                  expandedPaths={expandedPaths}
                  onTogglePath={handleTogglePath}
                />
              </Paper>
            )}
          </Stack>
        </Tabs.Panel>

        {/* Raw JSON */}
        <Tabs.Panel value="json" pt="md">
          <SimpleGrid cols={2}>
            <div>
              <Text size="sm" fw={500} mb="xs">Override Data</Text>
              <Code
                block
                style={{ maxHeight: 400, overflow: 'auto' }}
                dangerouslySetInnerHTML={{
                  __html: syntaxHighlight(override),
                }}
              />
            </div>
            <div>
              <Text size="sm" fw={500} mb="xs">Affected Fields ({composition.affectedFields.length})</Text>
              <Code
                block
                style={{ maxHeight: 400, overflow: 'auto' }}
                dangerouslySetInnerHTML={{
                  __html: syntaxHighlight(
                    composition.affectedFields.map((f: AffectedField) => ({
                      name: f.name,
                      type: f.fieldType,
                      change: f.changeType,
                      ...(f.newLabel ? { label: `${f.originalLabel} → ${f.newLabel}` } : {}),
                    }))
                  ),
                }}
              />
            </div>
          </SimpleGrid>
        </Tabs.Panel>

        {/* Safety Analysis */}
        <Tabs.Panel value="safety" pt="md">
          <Stack gap="md">
            <Paper p="md" withBorder>
              <Group gap="xs" mb="sm">
                <ThemeIcon size="sm" variant="light" color={
                  schema.safetyLevel === 'LIFE_CRITICAL' ? 'red' :
                  schema.safetyLevel === 'CRITICAL' ? 'orange' :
                  schema.safetyLevel === 'ELEVATED' ? 'yellow' : 'green'
                }>
                  <IconShieldCheck size={12} />
                </ThemeIcon>
                <Text size="sm" fw={500}>Form Safety Level: {String(schema.safetyLevel)}</Text>
              </Group>

              {composition.safetyWarnings.length === 0 ? (
                <Text size="sm" c="green">No safety concerns with current overrides.</Text>
              ) : (
                <Stack gap="xs">
                  {composition.safetyWarnings.map((w: SafetyWarning, i: number) => (
                    <Paper key={i} p="xs" withBorder style={{ borderColor: 'var(--mantine-color-red-3)' }}>
                      <Group gap="xs">
                        <Badge size="xs" color="red">{w.changeType}</Badge>
                        <Text size="xs" fw={500}>{w.fieldName}</Text>
                      </Group>
                      <Text size="xs" c="dimmed" mt={2}>{w.message}</Text>
                    </Paper>
                  ))}
                </Stack>
              )}
            </Paper>

            <Paper p="md" withBorder>
              <Text size="sm" fw={500} mb="sm">PII-Classified Fields</Text>
              {schema.fields.filter(f => f.piiClassification).length === 0 ? (
                <Text size="xs" c="dimmed">No PII-classified fields in this schema.</Text>
              ) : (
                <Stack gap="xs">
                  {schema.fields.filter(f => f.piiClassification).map(f => {
                    const isHidden = override?.hiddenFields?.includes(f.name);
                    const isReadonly = override?.readonlyFields?.includes(f.name);
                    return (
                      <Group key={f.name} gap="xs">
                        <Badge size="xs" variant="outline" color="violet">{f.piiClassification}</Badge>
                        <Text size="xs">{f.label || f.name}</Text>
                        {isHidden && <Badge size="xs" color="red">HIDDEN</Badge>}
                        {isReadonly && <Badge size="xs" color="orange">READONLY</Badge>}
                        {!isHidden && !isReadonly && <Badge size="xs" color="green">VISIBLE</Badge>}
                      </Group>
                    );
                  })}
                </Stack>
              )}
            </Paper>
          </Stack>
        </Tabs.Panel>
      </Tabs>
    </Stack>
  );
}
